import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { isNil } from 'lodash';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { AppConfig } from '@app/app.config';
import { ANALYTICS_EVENTS, AnalyticsService } from '@app/modules/analytics';
import { AuthService } from '@shared/services';
import { CommonDialogsService } from '@shared/services/common-dialogs';

const nonAuthPaths: Record<string, string[]> = {
    GET: ['^/api/rest/v2/exports/downloads'],
    any: [
        '^/api/rest/v2/security/tokens/(identity|authentication|access|verification)',
        '^/favicons/',
        '^/static/shared/',
        '^/security/tokens/verification/',
    ],
};

const nonAuthPathsRegexp = (method: string): RegExp => {
    const paths = nonAuthPaths[method.toUpperCase()] || [];
    const anyPaths = nonAuthPaths.any || [];
    return new RegExp([...paths, ...anyPaths].join('|'));
};

let isRefreshingToken = false;
const tokenSubject = new BehaviorSubject<string>(null);

export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);
    const config = inject(AppConfig);
    const analyticsService = inject(AnalyticsService);
    const commonDialogsService = inject(CommonDialogsService);

    const isAuthorizedRequest = (request: HttpRequest<unknown>): boolean => {
        try {
            return !nonAuthPathsRegexp(request.method).test(new URL(request.url).pathname);
        } catch {
            return false;
        }
    };

    const addToken = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> =>
        request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`,
            },
        });

    const showSessionExpiredModal = (): void => {
        const canRedirectToLogin = !config.appInfo.integration;
        const message = canRedirectToLogin ? 'Please relogin' : 'Please reload the page';

        commonDialogsService
            .fire({
                html: `Current session is expired. ${message}`,
                confirmButtonText: 'Ok',
                showConfirmButton: canRedirectToLogin,
                panelClass: 'dialog-invalid-session-handler-component',
            })
            .subscribe(() => {
                authService.logout({ saveOriginUrl: true, redirectToLogin: true });
                analyticsService.trackEvent(ANALYTICS_EVENTS.USER_LOGGED_OUT);
            });
    };

    const handle401Error = (request: HttpRequest<unknown>): ReturnType<HttpInterceptorFn> => {
        if (!isRefreshingToken) {
            isRefreshingToken = true;
            tokenSubject.next(null);

            return authService.refreshAccessToken().pipe(
                catchError((err) => {
                    isRefreshingToken = false;
                    showSessionExpiredModal();
                    return throwError(() => err);
                }),
                switchMap((newToken: string) => {
                    isRefreshingToken = false;
                    tokenSubject.next(newToken);
                    return next(addToken(request, newToken));
                }),
            );
        }
        return tokenSubject.pipe(
            filter((token) => token != null),
            take(1),
            switchMap((token) => next(addToken(request, token))),
        );
    };

    let request = req;
    const accessToken = authService.accessToken;
    if (!isNil(accessToken) && isAuthorizedRequest(request)) {
        request = addToken(request, accessToken);
    }

    return next(request).pipe(
        catchError((error: Error) => {
            if (error instanceof HttpErrorResponse) {
                switch (error.status) {
                    case 401:
                        if (authService.authToken && isAuthorizedRequest(request)) {
                            return handle401Error(request);
                        }
                        break;
                    case 403:
                        return throwError(() => error);
                    default:
                        return throwError(() => error);
                }
            }
            return throwError(() => error);
        }),
    );
};
